import React, { createContext, useContext, useState, ReactNode, useRef, useCallback, useEffect } from 'react';
import {
  GameState,
  GameMode,
  GameConfig,
  GameStatus,
  GameHistory,
  saveGameConfig,
  clearGame,
  isValidToPlayEncore,
  saveGame,
  craftGameStorageKey,
  craftHistoryStorageKey
} from '@/utils/gameState';
import { loadAllStorageData, AllStorageData, storageWriter } from '@/utils/storageManager';
import {
  getTargetWord
} from '@/utils/gameUtils';
import { getNormalizedDate, getDateString } from '@/utils/dateUtils';
import { SecureTargetWord } from '@/utils/wordUtils';
import { isEmptyWord } from '@/utils/cryptoUtils';
import {
  buildLevelName,
  buildPlayedDate,
  buildPuzzleDate,
  buildPuzzleId,
  trackEvent
} from '@/utils/analytics';

interface GameContextType {
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  currentGuess: string;
  setCurrentGuess: React.Dispatch<React.SetStateAction<string>>;
  gameConfig: GameConfig;
  setGameConfig: React.Dispatch<React.SetStateAction<GameConfig>>;
  gameCompletions: Record<string, GameStatus>;
  setGameCompletions: React.Dispatch<React.SetStateAction<Record<string, GameStatus>>>;
  showResult: boolean;
  setShowResult: React.Dispatch<React.SetStateAction<boolean>>;
  showHelpInfo: boolean;
  setShowHelpInfo: React.Dispatch<React.SetStateAction<boolean>>;
  showArchive: boolean;
  setShowArchive: React.Dispatch<React.SetStateAction<boolean>>;
  revealAnimationComplete: boolean;
  setRevealAnimationComplete: React.Dispatch<React.SetStateAction<boolean>>;
  dailyGameState: GameState | null;
  setDailyGameState: React.Dispatch<React.SetStateAction<GameState | null>>;
  canPlayEncore: boolean;
  setCanPlayEncore: React.Dispatch<React.SetStateAction<boolean>>;
  timerActive: boolean;
  setTimerActive: React.Dispatch<React.SetStateAction<boolean>>;
  hintLetter: string | null;
  setHintLetter: React.Dispatch<React.SetStateAction<string | null>>;
  initializeGame: (config: GameConfig) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

const initialGameState: GameState = {
  guesses: [],
  evaluations: [],
  targetWord: {
    salt: "",
    hashedLetters: [
      "",
      "",
      "",
      ""
    ],
  },
  targetWordNumber: null,
  targetWordDate: null,
  gameStatus: GameStatus.IN_PROGRESS,
  currentRowIndex: 0,
  keyboardState: {},
  lastCompletedTime: null,
  error: null
};

const initialGameConfig: GameConfig = {
  mode: GameMode.DAILY,
  date: getNormalizedDate()
};

const getHistoryMode = (mode: GameMode): GameMode => {
  return mode === GameMode.ARCHIVE ? GameMode.DAILY : mode;
};

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameConfig, setGameConfig] = useState<GameConfig>(initialGameConfig);
  const [gameCompletions, setGameCompletions] = useState<Record<string, GameStatus>>({});
  const [showResult, setShowResult] = useState(false);
  const [showHelpInfo, setShowHelpInfo] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [revealAnimationComplete, setRevealAnimationComplete] = useState(true);
  const [gameState, setGameState] = useState<GameState>(initialGameState);
  const [canPlayEncore, setCanPlayEncore] = useState(false);
  const [dailyGameState, setDailyGameState] = useState<GameState | null>(null);
  const [timerActive, setTimerActive] = useState(false);
  const [hintLetter, setHintLetter] = useState<string | null>(null);
  const [allStorageData, setAllStorageData] = useState<AllStorageData | null>(null);
  const hasInitialized = useRef(false);

  const initializeGame = useCallback((config: GameConfig) => {
    const [targetWord, targetWordNumber] = getTargetWord(config.mode, config.date);
    const puzzleDate = buildPuzzleDate(config.mode, config.date);
    const puzzleId = buildPuzzleId(config.mode, config.date);

    const newGameState: GameState = {
      guesses: [],
      evaluations: [],
      targetWord: targetWord as SecureTargetWord,
      targetWordNumber: targetWordNumber,
      targetWordDate: config.date ?? getNormalizedDate(),
      gameStatus: GameStatus.IN_PROGRESS,
      currentRowIndex: 0,
      keyboardState: {},
      lastCompletedTime: null,
      error: null,
    };

    trackEvent('level_start', {
      level_name: buildLevelName(config.mode, config.date),
      puzzle_id: puzzleId,
      game_mode: config.mode,
      played_date: buildPlayedDate(),
      puzzle_date: puzzleDate
    });

    setGameState(newGameState);
    setCurrentGuess('');
    setRevealAnimationComplete(true);
    setShowResult(false);

    // Update in-memory storage with new game
    setAllStorageData(prev => {
      if (!prev) return prev;
      const gameKey = craftGameStorageKey(config.mode, config.date);
      return {
        ...prev,
        games: {
          ...prev.games,
          [gameKey]: newGameState
        }
      };
    });

    // Clear from localStorage (debounced)
    clearGame(config.mode, config.date);
  }, [setGameState, setCurrentGuess, setRevealAnimationComplete, setShowResult, setAllStorageData]);

  // Update history in memory and persist to localStorage
  const updateHistory = useCallback((updates: Partial<GameHistory>, mode: GameMode) => {
    setAllStorageData(prev => {
      if (!prev) return prev;

      const historyMode = getHistoryMode(mode);
      const currentHistory = prev.histories[historyMode] || {};
      const updatedHistory = {
        ...currentHistory,
        ...updates
      };

      // Update in-memory
      const newData = {
        ...prev,
        histories: {
          ...prev.histories,
          [historyMode]: updatedHistory
        }
      };

      // Write to localStorage (debounced)
      const key = craftHistoryStorageKey(historyMode);
      storageWriter.queueWrite(key, updatedHistory);

      return newData;
    });
  }, [setAllStorageData]);


  // Load ALL data from localStorage ONCE on initialization
  useEffect(() => {
    if (!hasInitialized.current) {
      // Load ALL data from localStorage ONCE
      const storageData = loadAllStorageData();
      setAllStorageData(storageData);

      // Set config from loaded data
      setGameConfig(storageData.config);

      // Load or initialize the current game using in-memory data
      const currentGameKey = craftGameStorageKey(
        storageData.config.mode,
        storageData.config.date
      );
      const savedGame = storageData.games[currentGameKey];

      // Check if we should load the saved game
      // Random: always load if exists
      // Daily/Encore/Archive: load if the saved game's date matches the config date
      const shouldLoadSavedGame = savedGame && (
        storageData.config.mode === GameMode.RANDOM ||
        (savedGame.targetWordDate !== null &&
         getDateString(savedGame.targetWordDate) === getDateString(storageData.config.date))
      ) && !isEmptyWord(savedGame.targetWord);

      if (shouldLoadSavedGame) {
        setGameState(savedGame);
      } else {
        initializeGame(storageData.config);
      }

      // Load hint letter and completions from in-memory data
      const history = storageData.histories[storageData.config.mode] || {};
      const dailyHistory = storageData.histories[GameMode.DAILY] || {};
      setHintLetter(history.hintLetter || null);
      setGameCompletions(dailyHistory.completions || {});

      // Show help if first time - check RANDOM mode history since that's where we store it
      const randomHistory = storageData.histories[GameMode.RANDOM] || {};
      if (!randomHistory.hasPlayedBefore) {
        setShowHelpInfo(true);
        updateHistory({ hasPlayedBefore: true }, GameMode.RANDOM);
      }

      hasInitialized.current = true;
    }
  }, []); // Empty deps - runs exactly once

  // Handle game mode switching using in-memory data only
  useEffect(() => {
    if (!hasInitialized.current || !allStorageData) return;

    // Get the game state from in-memory storage
    const gameKey = craftGameStorageKey(gameConfig.mode, gameConfig.date);
    const savedGame = allStorageData.games[gameKey];

    // Check if we should load the saved game
    // Random: always load if exists
    // Daily/Encore/Archive: load if the saved game's date matches the config date
    const shouldLoadSavedGame = savedGame && (
      gameConfig.mode === GameMode.RANDOM ||
      (savedGame.targetWordDate !== null &&
       getDateString(savedGame.targetWordDate) === getDateString(gameConfig.date))
    ) && !isEmptyWord(savedGame.targetWord);

    if (shouldLoadSavedGame) {
      // Load saved game from memory
      setGameState(savedGame);
    } else {
      // Initialize new game
      initializeGame(gameConfig);
    }

    // Update hint letter and completions from in-memory history
    const history = allStorageData.histories[gameConfig.mode] || {};
    const dailyHistory = allStorageData.histories[GameMode.DAILY] || {};
    setHintLetter(history.hintLetter || null);
    setGameCompletions(dailyHistory.completions || {});

    // Reset showResult when switching modes to prevent stale modals
    setShowResult(false);

    // Save config change to localStorage (debounced)
    saveGameConfig(gameConfig);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameConfig]); // Only re-run when gameConfig changes, not when allStorageData changes
  
  // Determine if the user can play Encore on game mode and result modal pop-up
  useEffect(() => {
    if (!allStorageData) return;

    const canPlay = isValidToPlayEncore(
      allStorageData.histories[GameMode.DAILY] || {},
      allStorageData.histories[GameMode.ENCORE] || {}
    );
    setCanPlayEncore(canPlay);
  }, [gameConfig.mode, showResult, allStorageData]);
  
  // Saves game to localStorage AND in-memory storage
  useEffect(() => {
    // Don't save the initial empty state - it would overwrite correctly initialized games
    if (isEmptyWord(gameState.targetWord)) {
      return;
    }

    // Save to localStorage (debounced)
    saveGame(gameState, gameConfig.mode, gameConfig.date);

    // Update in-memory storage immediately
    setAllStorageData(prev => {
      if (!prev) return prev;
      const gameKey = craftGameStorageKey(gameConfig.mode, gameConfig.date);
      return {
        ...prev,
        games: {
          ...prev.games,
          [gameKey]: gameState
        }
      };
    });
  }, [gameState, gameConfig.mode, gameConfig.date]);

  // Saves completed dates to localStorage
  useEffect(() => {
    if (!allStorageData) return;

    // If the gameConfig and the gameState are out of sync, skip saving
    if (getDateString(gameConfig.date) !== getDateString(gameState.targetWordDate)) return;

    if (gameState.gameStatus !== GameStatus.IN_PROGRESS && gameState.lastCompletedTime) {
      const completedDate = getNormalizedDate(new Date(gameState.lastCompletedTime));
      const targetWordDate = gameConfig.date ?? getNormalizedDate(new Date());
      const targetWordDateString = getDateString(getNormalizedDate(targetWordDate));
      const completedDateString = getDateString(completedDate);
      const historyMode = getHistoryMode(gameConfig.mode);
      const history = allStorageData.histories[historyMode] || {};
      const priorCompletions = history.completions || {};

      if (priorCompletions[targetWordDateString] === gameState.gameStatus) return;

      const updatedCompletions = {
        ...priorCompletions,
        [targetWordDateString]: gameState.gameStatus
      };

      updateHistory({
        lastCompletedDate: completedDateString,
        completions: updatedCompletions
      }, historyMode);

      if (historyMode === GameMode.DAILY) {
        setGameCompletions(updatedCompletions);
      }
    }
  }, [allStorageData, gameState.gameStatus, gameState.lastCompletedTime, gameConfig.mode, gameConfig.date, gameState.targetWordDate, updateHistory]);

  return (
    <GameContext.Provider value={{
      gameState,
      setGameState,
      currentGuess,
      setCurrentGuess,
      gameConfig,
      setGameConfig,
      gameCompletions,
      setGameCompletions,
      showResult,
      setShowResult,
      showHelpInfo,
      setShowHelpInfo,
      showArchive,
      setShowArchive,
      revealAnimationComplete,
      setRevealAnimationComplete,
      dailyGameState,
      setDailyGameState,
      canPlayEncore,
      setCanPlayEncore,
      timerActive,
      setTimerActive,
      hintLetter,
      setHintLetter,
      initializeGame
    }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
